import React, { useState } from "react";
import * as api from "../api/writtenContribution";
import { API_ROOT } from "../api";
import "./WrittenContributionsForm.css";

const COVER_MAX_BYTES = 5 * 1024 * 1024;
const PDF_MAX_BYTES = 50 * 1024 * 1024;
const COVER_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"];

const formatFileSizeMb = (bytes: number) =>
  `${(bytes / (1024 * 1024)).toFixed(1)}MB`;

function validatePublicationUrl(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed);
    if (!["http:", "https:"].includes(parsed.protocol)) {
      return "URL must start with http:// or https://";
    }
    return null;
  } catch {
    return "Please enter a valid URL";
  }
}

interface Props {
  token: string;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
}

const WrittenContributionsForm: React.FC<Props> = ({
  token,
  onClose,
  onSuccess,
}) => {
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [isDownloadable, setIsDownloadable] = useState(true);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [publicationUrl, setPublicationUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateCoverFile = (file: File): string | null => {
    if (!COVER_MIME_TYPES.includes(file.type)) {
      return "Cover image must be JPEG, PNG, or WebP.";
    }
    if (file.size > COVER_MAX_BYTES) {
      return `Cover image must be ${formatFileSizeMb(COVER_MAX_BYTES)} or smaller. Your file is ${formatFileSizeMb(file.size)}.`;
    }
    return null;
  };

  const validatePdfFile = (file: File): string | null => {
    if (file.type !== "application/pdf") {
      return "Only PDF files are allowed.";
    }
    if (file.size > PDF_MAX_BYTES) {
      return `PDF must be ${formatFileSizeMb(PDF_MAX_BYTES)} or smaller. Your file is ${formatFileSizeMb(file.size)}.`;
    }
    return null;
  };

  // Upload cover to cloudinary via backend endpoint
  const handleUploadCover = async (): Promise<{
    secure_url: string;
    public_id: string;
    bytes: number;
    mimeType?: string;
  } | null> => {
    if (!coverFile) return null;
    try {
      const res = await api.callUploadCover(coverFile, token);
      return {
        secure_url: res.secure_url,
        public_id: res.public_id,
        bytes: res.bytes,
        mimeType: res.mimeType || coverFile.type,
      };
    } catch (err: any) {
      console.error("Cover upload error:", err);
      setError(
        err?.response?.data?.message || err.message || "Cover upload failed",
      );
      return null;
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);

    if (!title) {
      setError("Title is required");
      return;
    }

    if (!coverFile) {
      setError("Cover image is required");
      return;
    }

    const coverValidationError = validateCoverFile(coverFile);
    if (coverValidationError) {
      setError(coverValidationError);
      return;
    }

    const trimmedUrl = publicationUrl.trim();
    const hasPdf = Boolean(pdfFile);
    const hasUrl = Boolean(trimmedUrl);

    if (!hasPdf && !hasUrl) {
      setError("Please provide either a PDF file or a publication URL");
      return;
    }

    if (hasPdf && hasUrl) {
      setError("Please provide either a PDF or a URL, not both");
      return;
    }

    if (hasUrl) {
      const urlValidationError = validatePublicationUrl(trimmedUrl);
      if (urlValidationError) {
        setError(urlValidationError);
        return;
      }
    }

    if (hasPdf && pdfFile) {
      const pdfValidationError = validatePdfFile(pdfFile);
      if (pdfValidationError) {
        setError(pdfValidationError);
        return;
      }
    }

    setUploading(true);

    try {
      const coverRes = await handleUploadCover();
      if (!coverRes) {
        setUploading(false);
        return;
      }

      if (hasUrl) {
        await api.callCreateContribution(
          {
            title,
            fileUrl: trimmedUrl,
            publicId: `external-link-${Date.now()}`,
            mimeType: "text/uri-list",
            fileSizeBytes: 0,
            summary: summary || "",
            isDownloadable: false,
            publicationCover: coverRes.secure_url,
            publicationCoverPublicId: coverRes.public_id,
            publicationCoverMimeType: coverRes.mimeType || "",
            publicationCoverSizeBytes: coverRes.bytes,
          },
          token,
        );
      } else if (pdfFile) {
        const form = new FormData();
        form.append("file", pdfFile);
        form.append("title", title);
        form.append("summary", summary || "");
        form.append("isDownloadable", String(isDownloadable));
        form.append("publicationCover", coverRes.secure_url);
        form.append("publicationCoverPublicId", coverRes.public_id);
        form.append("publicationCoverMimeType", coverRes.mimeType || "");
        form.append("publicationCoverSizeBytes", String(coverRes.bytes));

        const createRes = await fetch(`${API_ROOT}/contributions/upload`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: form,
        });

        if (!createRes.ok) {
          const errJson = await createRes.json().catch(() => ({}));
          throw new Error(
            errJson?.message || `Upload failed with status ${createRes.status}`,
          );
        }
      }

      await onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error("Submit error:", err);
      setError(err?.message || "Failed to create contribution");
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="publication-form-container">
      <p>Let's share Knowledge to make this planet a better place.</p>
      <br />
      {/* <div className=""> */}
      <label className="sr-only">Title</label>
      <input
        placeholder="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      {/* </div> */}

      {/* <div className=""> */}
      <label className="sr-only">Summary (optional)</label>
      <textarea
        placeholder="Summary (optional)"
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        rows={3}
      />
      {/* </div> */}

      <div className="written-contributions-form-input-single">
        <label>Cover image (JPEG/PNG/WebP, max 5MB) *</label>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) {
              setCoverFile(null);
              return;
            }

            const validationError = validateCoverFile(f);
            if (validationError) {
              setError(validationError);
              setCoverFile(null);
              e.target.value = "";
              return;
            }

            setError(null);
            setCoverFile(f);
          }}
        />
      </div>

      <div className="written-contributions-form-input-single">
        <label>Publication URL</label>
        <input
          type="url"
          placeholder="https://example.com/your-publication"
          value={publicationUrl}
          onChange={(e) => {
            const nextUrl = e.target.value;
            setPublicationUrl(nextUrl);
            if (nextUrl.trim()) {
              setPdfFile(null);
              setIsDownloadable(false);
            }
            setError(null);
          }}
          disabled={Boolean(pdfFile)}
        />
      </div>

      <div className="written-contributions-form-input-single">
        <label>PDF file (max 50MB)</label>
        <input
          type="file"
          accept="application/pdf"
          disabled={Boolean(publicationUrl.trim())}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) {
              setPdfFile(null);
              return;
            }

            const validationError = validatePdfFile(f);
            if (validationError) {
              setError(validationError);
              setPdfFile(null);
              e.target.value = "";
              return;
            }

            setPublicationUrl("");
            setError(null);
            setPdfFile(f);
          }}
        />
      </div>

      <p className="publication-form-hint">
        Provide either a URL or a PDF (one is required).
      </p>

      {pdfFile && (
        <div className="downloadable-pdf-pair">
          <input
            id="isDownloadable"
            style={{ width: "" }}
            type="checkbox"
            checked={isDownloadable}
            onChange={() => setIsDownloadable((val) => !val)}
          />
          <label className="bold" htmlFor="isDownloadable">
            Make PDF downloadable
          </label>
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="buttons-footer">
        <button
          type="button"
          className="btn equal-btns"
          onClick={onClose}
          disabled={uploading}
        >
          Cancel
        </button>
        <button type="submit" className="btn equal-btns" disabled={uploading}>
          {uploading ? "Uploading..." : "Post"}
        </button>
      </div>
    </form>
  );
};

export default WrittenContributionsForm;
